/**
 * Cookie consent: the single source of truth for what a visitor agreed to.
 *
 * Until now the banner wrote a `cookieConsent` cookie and then read it back
 * only to decide whether to show itself again. Nothing else in the app looked
 * at it, so choosing "Essential only" changed nothing: Intercom still booted,
 * Stripe.js still loaded on every page. This module exists so there is one
 * place that answers "may we load X?", and so the answer is the same
 * everywhere.
 *
 * Categories are deliberately limited to what Attractr actually runs. A
 * toggle for tracking we do not do is not a neutral extra: it tells the
 * visitor we profile them for advertising, and it makes our own consent
 * record describe something that never happened.
 */

export const COOKIE_CONSENT_NAME = 'cookieConsent';

export const CONSENT_CATEGORIES = {
  ESSENTIAL: 'essential',
  FUNCTIONAL: 'functional',
  STATISTICS: 'statistics',
};

// Essential is not a choice: without it there is no session, no checkout and
// no consent record. It is listed so the banner can say so plainly.
export const ALWAYS_ON = [CONSENT_CATEGORIES.ESSENTIAL];

// The categories a visitor can actually decide, in display order.
export const OPTIONAL_CATEGORIES = [
  CONSENT_CATEGORIES.FUNCTIONAL,
  CONSENT_CATEGORIES.STATISTICS,
];

export const DEFAULT_PREFERENCES = {
  [CONSENT_CATEGORIES.ESSENTIAL]: true,
  [CONSENT_CATEGORIES.FUNCTIONAL]: false,
  [CONSENT_CATEGORIES.STATISTICS]: false,
};

/**
 * Bump when the categories or the wording behind them change materially.
 * Stored with the decision so a record can be read back against the version of
 * the banner the person actually saw, rather than against today's copy.
 */
export const CONSENT_VERSION = 2;

// How long the banner remembers a choice before asking again. Thirteen months,
// per the Cookies Policy (26 Sep 2026). The consent record itself is retained
// separately for 24 months, on the profile and in the consent archive.
const COOKIE_MAX_AGE_DAYS = 13 * 30 + 5;

const isBrowser = () => typeof document !== 'undefined';

const parseCookies = () =>
  document.cookie.split('; ').reduce((acc, part) => {
    const idx = part.indexOf('=');
    if (idx < 0) return acc;
    const name = part.slice(0, idx);
    const value = part.slice(idx + 1);
    try {
      acc[name] = decodeURIComponent(value);
    } catch (e) {
      acc[name] = value;
    }
    return acc;
  }, {});

/**
 * Normalise a stored decision to the current category set.
 *
 * Version 1 of the banner stored `preferences` and `marketing`. `preferences`
 * described the same thing this version calls `functional`, so it carries
 * across rather than making someone who already answered answer again.
 * `marketing` is dropped: we run no marketing tags, so there is nothing for it
 * to permit, and keeping a stale grant on file would be worse than losing it.
 */
const normalise = stored => {
  if (!stored || typeof stored !== 'object') return null;

  const functional =
    typeof stored.functional === 'boolean' ? stored.functional : !!stored.preferences;

  return {
    [CONSENT_CATEGORIES.ESSENTIAL]: true,
    [CONSENT_CATEGORIES.FUNCTIONAL]: functional,
    [CONSENT_CATEGORIES.STATISTICS]: !!stored.statistics,
    consentId: stored.consentId || null,
    version: stored.version || 1,
    timestamp: stored.timestamp || null,
  };
};

/** The visitor's stored decision, or null when they have not answered yet. */
export const readConsent = () => {
  if (!isBrowser()) return null;
  const raw = parseCookies()[COOKIE_CONSENT_NAME];
  if (!raw) return null;
  try {
    return normalise(JSON.parse(raw));
  } catch (e) {
    // A cookie we cannot read is the same as no answer: ask again rather than
    // assuming either way.
    return null;
  }
};

/** Random id tying a stored cookie to its server-side consent record. */
const newConsentId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * Persist a decision and return the exact record that was stored, so the
 * caller can send the same thing to the server log.
 */
export const writeConsent = preferences => {
  const record = {
    [CONSENT_CATEGORIES.ESSENTIAL]: true,
    [CONSENT_CATEGORIES.FUNCTIONAL]: !!preferences[CONSENT_CATEGORIES.FUNCTIONAL],
    [CONSENT_CATEGORIES.STATISTICS]: !!preferences[CONSENT_CATEGORIES.STATISTICS],
    consentId: preferences.consentId || newConsentId(),
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
  };

  if (isBrowser()) {
    const expires = new Date();
    expires.setDate(expires.getDate() + COOKIE_MAX_AGE_DAYS);
    document.cookie = [
      `${COOKIE_CONSENT_NAME}=${encodeURIComponent(JSON.stringify(record))}`,
      'path=/',
      `expires=${expires.toUTCString()}`,
      'SameSite=Lax',
    ].join('; ');
  }

  return record;
};

/**
 * May we load something in this category?
 *
 * Fails closed on purpose. `consent` is null both before the visitor has
 * answered and while the answer is still being read, and in either case the
 * honest answer is no.
 */
export const hasConsent = (consent, category) => {
  if (category === CONSENT_CATEGORIES.ESSENTIAL) return true;
  return !!consent && consent[category] === true;
};
